import React, { useState, useEffect } from 'react';
import { Megaphone, Plus, Trash2, EyeOff, Eye, Link, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  getAdminAnnouncements,
  createAnnouncement,
  deleteAnnouncement,
  patchAnnouncement,
} from '../../api';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function isExpired(expires_at) {
  return new Date(expires_at) < new Date();
}

export default function Announcements() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({ message: '', lien: '', expires_in_days: 7, actif: true });
  const [editId, setEditId]     = useState(null);
  const [editForm, setEditForm] = useState({ message: '', lien: '' });

  const load = async () => {
    try {
      const res = await getAdminAnnouncements();
      setItems(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error('Le message est obligatoire'); return; }
    setSaving(true);
    try {
      await createAnnouncement({
        message: form.message.trim(),
        lien: form.lien.trim() || null,
        expires_in_days: Number(form.expires_in_days) || 7,
        actif: form.actif,
      });
      toast.success('Annonce créée');
      setShowForm(false);
      setForm({ message: '', lien: '', expires_in_days: 7, actif: true });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      await patchAnnouncement(item.id, { actif: !item.actif });
      setItems(prev => prev.map(a => a.id === item.id ? { ...a, actif: !a.actif } : a));
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({ message: item.message, lien: item.lien || '' });
  };

  const cancelEdit = () => { setEditId(null); setEditForm({ message: '', lien: '' }); };

  const saveEdit = async (id) => {
    if (!editForm.message.trim()) { toast.error('Le message est obligatoire'); return; }
    try {
      const res = await patchAnnouncement(id, {
        message: editForm.message.trim(),
        lien: editForm.lien.trim() || null,
      });
      setItems(prev => prev.map(a => a.id === id ? { ...a, ...res.data } : a));
      toast.success('Annonce mise à jour');
      cancelEdit();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await deleteAnnouncement(id);
      setItems(prev => prev.filter(a => a.id !== id));
      toast.success('Annonce supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="max-w-3xl">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #1E40AF, #3B82F6)' }}>
            <Megaphone size={17} className="text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold" style={{ color: 'var(--text)' }}>Annonces globales</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Diffusées à tous les utilisateurs connectés
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          data-testid="announcement-create-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, #1E40AF, #3B82F6)' }}
        >
          <Plus size={15} />
          Nouvelle annonce
        </button>
      </div>

      {/* Formulaire de création */}
      {showForm && (
        <form
          onSubmit={handleCreate}
          data-testid="announcement-form"
          className="mb-6 rounded-xl border p-5 space-y-4"
          style={{ borderColor: 'var(--border)', background: '#F8FAFC' }}
        >
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
              style={{ color: 'var(--text-muted)' }}>Message *</label>
            <textarea
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              data-testid="announcement-message-input"
              required
              rows={3}
              placeholder="Ex : Nouvelle fonctionnalité disponible — Synchronisation Pennylane améliorée"
              className="w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100 resize-none"
              style={{ borderColor: 'var(--border-light)', color: 'var(--text)' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
                style={{ color: 'var(--text-muted)' }}>
                <Link size={11} className="inline mr-1" />Lien (optionnel)
              </label>
              <input
                value={form.lien}
                onChange={e => setForm(f => ({ ...f, lien: e.target.value }))}
                data-testid="announcement-lien-input"
                type="text"
                placeholder="/settings ou https://..."
                className="w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
                style={{ borderColor: 'var(--border-light)', color: 'var(--text)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5"
                style={{ color: 'var(--text-muted)' }}>Durée (jours)</label>
              <input
                value={form.expires_in_days}
                onChange={e => setForm(f => ({ ...f, expires_in_days: e.target.value }))}
                data-testid="announcement-duration-input"
                type="number"
                min={1}
                max={90}
                className="w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100"
                style={{ borderColor: 'var(--border-light)', color: 'var(--text)' }}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm"
              style={{ color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={form.actif}
                onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))}
                data-testid="announcement-actif-checkbox"
                className="rounded"
              />
              Activer immédiatement
            </label>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              data-testid="announcement-save-btn"
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #1E40AF, #3B82F6)' }}
            >
              {saving ? 'Création…' : 'Créer l\'annonce'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-5 py-2.5 rounded-xl text-sm font-medium border"
              style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* Liste */}
      {loading ? (
        <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>Chargement…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed"
          style={{ borderColor: 'var(--border-light)' }}>
          <Megaphone size={28} style={{ color: 'var(--text-disabled)', margin: '0 auto 8px' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucune annonce créée</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            const expired = isExpired(item.expires_at);
            const isEditing = editId === item.id;
            return (
              <div
                key={item.id}
                data-testid={`announcement-item-${item.id}`}
                className="rounded-xl border p-4"
                style={{
                  borderColor: isEditing ? '#3B82F6' : 'var(--border)',
                  background: isEditing ? '#EFF6FF' : expired ? '#FAFAFA' : item.actif ? '#EFF6FF' : 'white',
                  opacity: expired && !isEditing ? 0.6 : 1,
                  transition: 'border-color 0.15s',
                }}
              >
                {isEditing ? (
                  /* ── Mode édition ── */
                  <div className="space-y-3">
                    <textarea
                      value={editForm.message}
                      onChange={e => setEditForm(f => ({ ...f, message: e.target.value }))}
                      data-testid="announcement-edit-message"
                      rows={2}
                      autoFocus
                      className="w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-200 resize-none"
                      style={{ borderColor: '#93C5FD', color: 'var(--text)' }}
                    />
                    <input
                      value={editForm.lien}
                      onChange={e => setEditForm(f => ({ ...f, lien: e.target.value }))}
                      data-testid="announcement-edit-lien"
                      type="text"
                      placeholder="Lien (optionnel) — /settings ou https://..."
                      className="w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-200"
                      style={{ borderColor: '#93C5FD', color: 'var(--text)' }}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(item.id)}
                        data-testid="announcement-save-edit"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                        style={{ background: '#2563EB' }}
                      >
                        <Check size={13} /> Enregistrer
                      </button>
                      <button
                        onClick={cancelEdit}
                        data-testid="announcement-cancel-edit"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border"
                        style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}
                      >
                        <X size={13} /> Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Mode lecture ── */
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                        {item.message}
                      </p>
                      {item.lien && (
                        <p className="text-xs mt-0.5" style={{ color: 'var(--primary-solid)' }}>
                          <Link size={10} className="inline mr-1" />{item.lien}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                          Créée par {item.created_by} · {fmtDate(item.created_at)}
                        </span>
                        <span className="text-xs" style={{ color: expired ? '#EF4444' : 'var(--text-disabled)' }}>
                          Expire : {fmtDate(item.expires_at)}{expired ? ' (expirée)' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{
                          background: expired ? '#FEE2E2' : item.actif ? '#DCFCE7' : '#F3F4F6',
                          color: expired ? '#EF4444' : item.actif ? '#16A34A' : '#6B7280',
                        }}>
                        {expired ? 'Expirée' : item.actif ? 'Active' : 'Inactive'}
                      </span>

                      {/* Éditer */}
                      <button onClick={() => startEdit(item)}
                        data-testid={`announcement-edit-${item.id}`}
                        title="Modifier"
                        className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                        style={{ color: '#3B82F6' }}>
                        <Pencil size={14} />
                      </button>

                      {/* Toggle actif */}
                      {!expired && (
                        <button onClick={() => handleToggle(item)}
                          data-testid={`announcement-toggle-${item.id}`}
                          title={item.actif ? 'Désactiver' : 'Activer'}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                          style={{ color: 'var(--text-muted)' }}>
                          {item.actif ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      )}

                      {/* Supprimer */}
                      <button onClick={() => handleDelete(item.id)}
                        data-testid={`announcement-delete-${item.id}`}
                        title="Supprimer"
                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                        style={{ color: '#EF4444' }}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
